import React, { useEffect, useState } from 'react';
import { ConversationDetail } from '../types';
import { useAdminLang } from '../App';
import { ADMIN_LABELS } from '../i18n';
import './ConversationModal.scss';

const _adminCfg = (window as any)._adminEnv_ || {};
const ADMIN_BACKEND_URL = (_adminCfg.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const LANG_LABELS: Record<string, string> = { en: 'English', ar: 'Arabic', ur: 'Urdu' };

interface Props {
  conversation: ConversationDetail;
  onClose: () => void;
  onResolve: (id: string) => void;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function ConversationModal({ conversation, onClose, onResolve }: Props) {
  const { lang } = useAdminLang();
  const t = ADMIN_LABELS[lang];
  const [detailsOpen, setDetailsOpen] = useState(false); // collapsed by default

  const {
    id, messages, user_name, user_phone, language, agencies, status,
    human_requested, incident_number, created_at,
    location_lat, location_lon, location_address,
  } = conversation;

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const agencyBadges: { label: string; color: string }[] = [];

  // Always show a status badge — fallback to 'Open' for any unrecognised value
  if (status === 'resolved') {
    agencyBadges.push({ label: 'Resolved', color: 'resolved' });
  } else if (status === 'pending_human') {
    agencyBadges.push({ label: 'Pending', color: 'pending' });
  } else {
    agencyBadges.push({ label: 'Open', color: 'open' });
  }

  const agencyList = (agencies || []) as string[];
  if (agencyList.includes('police')) agencyBadges.push({ label: 'Police', color: 'police' });
  if (agencyList.includes('ambulance')) agencyBadges.push({ label: 'Ambulance', color: 'ambulance' });
  if (agencyList.includes('civil_defense')) agencyBadges.push({ label: 'Civil defence', color: 'civil' });
  if (agencyList.includes('fire')) agencyBadges.push({ label: 'Fire', color: 'police' });
  if (agencyList.includes('emergency_services')) agencyBadges.push({ label: 'Emergency Services', color: 'police' });
  if (agencyList.includes('coast_guard')) agencyBadges.push({ label: 'Coast Guard', color: 'police' });
  if (agencyList.includes('rescue')) agencyBadges.push({ label: 'Rescue', color: 'police' });
  if (human_requested) agencyBadges.push({ label: 'Call back', color: 'callback' });

  const mapsUrl = location_lat && location_lon
    ? `https://www.google.com/maps?q=${location_lat},${location_lon}`
    : null;

  const callerInitial = (user_name || 'A').charAt(0).toUpperCase();

  return (
    <div className="conv-modal" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="conv-modal-header">
          <div className="conv-modal-header-left">
            <div className="conv-modal-name">{user_name || 'Unknown Caller'}</div>
            <div className="conv-modal-meta">
              {incident_number && (
                <span className="conv-modal-incident">{incident_number}</span>
              )}
              <span className="conv-modal-date">{formatDate(created_at)} {formatTime(created_at)}</span>
            </div>
          </div>
          <button className="conv-modal-close" onClick={onClose} title={t.closeModal}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Status & agency badges + action buttons */}
        <div className="conv-modal-actions-row">
          <div className="conv-modal-badges">
            {agencyBadges.map(b => (
              <span key={b.label} className={`modal-badge modal-badge--${b.color}`}>{b.label}</span>
            ))}
          </div>
          <div className="conv-modal-btns">
            <button className="modal-btn-callback" onClick={() => {}}>
              {t.callBackAction}
            </button>
            {status !== 'resolved' && (
              <button className="modal-btn-resolve" onClick={() => onResolve(id)}>
                {t.markAsResolved}
              </button>
            )}
          </div>
        </div>

        {/* Collapsible call details */}
        <div className="conv-modal-details">
          <button
            className="details-accordion-toggle"
            onClick={() => setDetailsOpen(o => !o)}
          >
            <span>{t.callDetails}</span>
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              style={{ transform: detailsOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
            >
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>

          {detailsOpen && (
            <div className="details-grid">
              {/* Row 1: Address | Name | Phone */}
              {(location_address || location_lat) && (
                <div className="details-cell">
                  <div className="details-cell-label">CALLER ADDRESS</div>
                  <div className="details-cell-value">
                    {location_address || `${location_lat?.toFixed(4)}, ${location_lon?.toFixed(4)}`}
                    {mapsUrl && (
                      <a href={mapsUrl} target="_blank" rel="noreferrer" className="details-link details-map-link">
                        {' '}({t.viewOnMap})
                      </a>
                    )}
                  </div>
                </div>
              )}
              <div className="details-cell">
                <div className="details-cell-label">CALLER NAME</div>
                <div className="details-cell-value">{user_name || '—'}</div>
              </div>
              <div className="details-cell">
                <div className="details-cell-label">CALLER NUMBER</div>
                <div className="details-cell-value">
                  {user_phone
                    ? <a href={`tel:${user_phone}`} className="details-link">{user_phone}</a>
                    : '—'}
                </div>
              </div>
              {/* Row 2: Language | Date & Time */}
              <div className="details-cell">
                <div className="details-cell-label">LANGUAGE</div>
                <div className="details-cell-value">{LANG_LABELS[language] || language}</div>
              </div>
              <div className="details-cell details-cell--span2">
                <div className="details-cell-label">DATE &amp; TIME</div>
                <div className="details-cell-value">{formatDateTime(created_at)}</div>
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="conv-modal-messages" style={{ paddingBottom: 0 }}>
          {(!messages || messages.length === 0) && (
            <div className="modal-no-messages">{t.noMessages}</div>
          )}
          {messages && messages.map(msg => {
            const isUser = msg.role === 'user';
            const initial = isUser ? callerInitial : 'D';
            return (
              <div key={msg.id} className={`modal-msg-row ${isUser ? 'user' : 'assistant'}`}>
                {/* Avatar: always show, both use green */}
                <div className={`modal-msg-avatar ${isUser ? 'user-avatar' : 'assistant-avatar'}`}>
                  {initial}
                </div>

                <div className={`modal-msg-bubble ${isUser ? 'user-bubble' : 'assistant-bubble'}`}>
                  {msg.media_url && (
                    <div className="modal-msg-image">
                      <a href={`${ADMIN_BACKEND_URL}${msg.media_url}`} target="_blank" rel="noreferrer">
                        <img
                          src={`${ADMIN_BACKEND_URL}${msg.media_url}`}
                          alt="Uploaded evidence"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      </a>
                    </div>
                  )}
                  {msg.content && msg.content !== '[IMAGE UPLOADED]' && (
                    <div className="modal-msg-content">{msg.content}</div>
                  )}
                  <div className="modal-msg-time">{formatDateTime(msg.timestamp)}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile-only action footer: Call back + Mark as resolved */}
        <div className="mobile-modal-footer">
          <button className="mobile-footer-callback" onClick={() => {}}>
            {t.callBackAction}
          </button>
          <button
            className={`mobile-footer-resolve${status === 'resolved' ? ' resolved' : ''}`}
            onClick={() => status !== 'resolved' && onResolve(id)}
            disabled={status === 'resolved'}
          >
            {status === 'resolved' ? t.markResolved : t.markAsResolved}
          </button>
        </div>
    </div>
  );
}
